/**
 * Shared validation utilities for consistent validation across the app
 * Reduces redundancy and ensures consistent behavior for all verification flows
 */

/**
 * Email validation utilities
 */
export const EmailValidation = {
  /**
   * Sanitize email input by removing potentially dangerous characters
   * @param {string} email - The email to sanitize
   * @returns {string} - The sanitized email
   */
  sanitize(email) {
    if (!email) return '';
    return email.trim().toLowerCase().replace(/[<>""'&]/g, '');
  },

  /**
   * Validate email format using regex
   * @param {string} email - The email to validate
   * @returns {boolean} - True if valid email format
   */
  isValid(email) {
    const emailRegex = /^[a-zA-Z0-9._%+-]+@[a-zA-Z0-9.-]+\.[a-zA-Z]{2,}$/;
    return emailRegex.test(email);
  },

  /**
   * Sanitize and validate email in one step
   * @param {string} email - The email to sanitize and validate
   * @returns {object} - { isValid: boolean, sanitized: string, error?: string }
   */
  process(email) {
    const sanitized = this.sanitize(email);
    const isValid = this.isValid(sanitized);
    
    return {
      isValid,
      sanitized,
      error: isValid ? null : 'Please enter a valid email address'
    };
  }
};

/**
 * Verification code validation utilities
 */
export const VerificationCodeValidation = {
  /**
   * Sanitize verification code by allowing only digits
   * @param {string} code - The code to sanitize
   * @returns {string} - The sanitized code (max 6 digits)
   */
  sanitize(code) {
    if (!code) return '';
    // Only allow digits, exactly 6 characters
    const digits = code.replace(/[^\d]/g, '');
    return digits.substring(0, 6);
  },

  /**
   * Validate verification code format (exactly 6 digits)
   * @param {string} code - The code to validate
   * @returns {boolean} - True if valid code format
   */
  isValid(code) {
    return code && code.length === 6 && /^\d{6}$/.test(code);
  },

  /**
   * Sanitize and validate verification code in one step
   * @param {string} code - The code to sanitize and validate
   * @returns {object} - { isValid: boolean, sanitized: string, error?: string }
   */
  process(code) {
    const sanitized = this.sanitize(code);
    const isValid = this.isValid(sanitized);
    
    return {
      isValid,
      sanitized,
      error: isValid ? null : 'Invalid verification code'
    };
  }
};

/**
 * Name validation utilities
 */
export const NameValidation = {
  /**
   * Sanitize name input by removing potentially dangerous characters
   * @param {string} name - The name to sanitize
   * @returns {string} - The sanitized name
   */
  sanitize(name) {
    if (!name) return '';
    // Remove potentially dangerous characters but allow letters, spaces, hyphens, apostrophes
    return name.trim().replace(/[^a-zA-Z\s\-']/g, '').substring(0, 100);
  },

  /**
   * Validate name format
   * @param {string} name - The name to validate
   * @returns {boolean} - True if valid name format
   */
  isValid(name) {
    return name && name.trim().length > 0 && /^[a-zA-Z\s\-']+$/.test(name.trim());
  },

  /**
   * Sanitize and validate name in one step
   * @param {string} name - The name to sanitize and validate
   * @returns {object} - { isValid: boolean, sanitized: string, error?: string }
   */
  process(name) {
    const sanitized = this.sanitize(name);
    const isValid = this.isValid(sanitized);
    
    return {
      isValid,
      sanitized,
      error: isValid ? null : 'Name must contain only letters, spaces, hyphens, and apostrophes'
    };
  }
};

/**
 * Attempt tracking utility for verification flows
 */
export class AttemptTracker {
  constructor(maxAttempts = 3) {
    this.maxAttempts = maxAttempts;
    this.attempts = 0;
  }

  /**
   * Increment attempt count
   * @returns {boolean} - True if max attempts not reached
   */
  increment() {
    this.attempts++;
    return this.attempts < this.maxAttempts;
  }

  /**
   * Get remaining attempts
   * @returns {number} - Number of remaining attempts
   */
  getRemainingAttempts() {
    return Math.max(0, this.maxAttempts - this.attempts);
  }

  /**
   * Check if max attempts reached
   * @returns {boolean} - True if max attempts reached
   */
  isMaxReached() {
    return this.attempts >= this.maxAttempts;
  }

  /**
   * Reset attempt count
   */
  reset() {
    this.attempts = 0;
  }

  /**
   * Get formatted error message for failed attempts
   * @returns {string} - Error message with remaining attempts
   */
  getErrorMessage() {
    const remaining = this.getRemainingAttempts();
    if (remaining === 0) {
      return 'Maximum attempts reached. Please request a new verification code.';
    }
    return `That wasn't the right code. You can try ${remaining} more time${remaining === 1 ? '' : 's'}.`;
  }
}

/**
 * Comprehensive verification flow helper
 * Combines validation, sanitization, and attempt tracking
 */
export class VerificationFlow {
  constructor(type = 'verification', maxAttempts = 3) {
    this.type = type;
    this.attemptTracker = new AttemptTracker(maxAttempts);
  }

  /**
   * Process and validate input data for verification flows
   * @param {object} data - Input data (email, code, name, etc.)
   * @returns {object} - Processed result with validation status
   */
  processInput(data) {
    const result = {
      isValid: true,
      sanitized: {},
      errors: []
    };

    // Process email if provided
    if (data.email !== undefined) {
      const emailResult = EmailValidation.process(data.email);
      result.sanitized.email = emailResult.sanitized;
      if (!emailResult.isValid) {
        result.isValid = false;
        result.errors.push(emailResult.error);
      }
    }

    // Process verification code if provided
    if (data.code !== undefined) {
      const codeResult = VerificationCodeValidation.process(data.code);
      result.sanitized.code = codeResult.sanitized;
      if (!codeResult.isValid) {
        result.isValid = false;
        result.errors.push(codeResult.error);
      }
    }

    // Process name if provided
    if (data.name !== undefined) {
      const nameResult = NameValidation.process(data.name);
      result.sanitized.name = nameResult.sanitized;
      if (!nameResult.isValid) {
        result.isValid = false;
        result.errors.push(nameResult.error);
      }
    }

    return result;
  }

  /**
   * Handle verification attempt with automatic attempt tracking
   * @param {function} verificationFunction - Async function that performs verification
   * @param {object} data - Data to pass to verification function
   * @returns {object} - Result with success status and error handling
   */
  async attemptVerification(verificationFunction, data) {
    try {
      const result = await verificationFunction(data);
      // Reset attempts on success
      this.attemptTracker.reset();
      return { success: true, result };
    } catch (error) {
      // Increment attempts on failure
      const canRetry = this.attemptTracker.increment();
      
      return {
        success: false,
        error: error.message,
        canRetry,
        remainingAttempts: this.attemptTracker.getRemainingAttempts(),
        isMaxReached: this.attemptTracker.isMaxReached(),
        attemptErrorMessage: this.attemptTracker.getErrorMessage()
      };
    }
  }

  /**
   * Reset the verification flow
   */
  reset() {
    this.attemptTracker.reset();
  }
}
